import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/Profile.css";

export default function ProfilePage() {
  const { user } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ✅ Helper: ensure /uploads/... resolves to your API origin if needed
  const absoluteUrl = (src) => {
    if (!src) return "";
    if (/^https?:\/\//i.test(src)) return src; // already absolute
    const base = (axios.defaults.baseURL || "").replace(/\/api\/?$/, "");
    return base ? `${base}${src}` : src;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get("/users/me", authHeader);
        if (!mounted) return;
        setProfile(res.data || null);
        setMessage("");
      } catch (err) {
        if (!mounted) return;
        setMessage(
          "❌ " +
            (err?.response?.data?.message ||
              err.message ||
              "Failed to load profile")
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [authHeader]);

  if (!token)
    return (
      <div className="user-manager-container profile-page">
        ⛔ Please log in to view your profile.
      </div>
    );

  if (!user)
    return (
      <div className="user-manager-container profile-page">
        🔄 Loading user info…
      </div>
    );

  const roleName = profile?.role || user?.role || "user";
  const isAllAccess = ["admin", "super-admin", "manager", "Manager"].includes(
    roleName
  );

  const displayName =
    profile?.fullName || profile?.name || profile?.username || "User";

  const avatarSrc = profile?.profilePic
    ? absoluteUrl(profile.profilePic)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}`;

  return (
    <div className="user-manager-container profile-page">
      <div className="profile-header">
        <div>
          <h2>My Profile</h2>
          <p className="profile-subtitle">
            You are currently signed in as <strong>{displayName}</strong>.
          </p>
        </div>
      </div>

      {message && (
        <div className="message-box profile-message">{message}</div>
      )}

      <div className="profile-layout">
        {/* LEFT: Main card */}
        <div className="card profile-main-card">
          <div className="profile-main-top">
            <div className="profile-avatar-wrap">
              <img
                className="avatar"
                src={avatarSrc}
                alt={displayName}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://ui-avatars.com/api/?name=User";
                }}
              />
            </div>

            <div className="profile-main-info">
              <h3 className="profile-name">{displayName}</h3>

              <div className="profile-role-row">
                <span className={`role-pill role-${String(roleName).toLowerCase()}`}>
                  {roleName}
                </span>
                {user?.email && (
                  <span className="profile-email">{user.email}</span>
                )}
              </div>

              {profile?.username && (
                <p className="profile-username">@{profile.username}</p>
              )}
            </div>
          </div>

          <div className="profile-fields-grid">
            <div className="profile-field">
              <label>Username</label>
              <div className="read-field">
                {profile?.username || user?.username || "—"}
              </div>
            </div>

            <div className="profile-field">
              <label>Role</label>
              <div className="read-field">{roleName}</div>
            </div>

            {/* Optional: show phone if it exists */}
            {profile?.phone && (
              <div className="profile-field">
                <label>Phone</label>
                <div className="read-field">{profile.phone}</div>
              </div>
            )}

            {/* Optional: show createdAt if your backend sends it */}
            {profile?.createdAt && (
              <div className="profile-field">
                <label>Member Since</label>
                <div className="read-field">
                  {new Date(profile.createdAt).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Access card */}
        <div className="card profile-access-card">
          <h3 className="section-title">Access & Permissions</h3>

          <div className="profile-field">
            <label>Master Site Access</label>
            {isAllAccess ? (
              <div className="read-field">All MasterSites</div>
            ) : Array.isArray(profile?.masterSiteAccess) &&
              profile.masterSiteAccess.length > 0 ? (
              <div className="pill-list">
                {profile.masterSiteAccess.map((ms) => (
                  <span
                    key={ms._id || ms.name || String(ms)}
                    className="access-pill"
                  >
                    {ms.name || String(ms)}
                  </span>
                ))}
              </div>
            ) : (
              <div className="read-field">—</div>
            )}
          </div>

          {/* You can add more permission info here later */}
          <div className="profile-hint">
            If you feel your access is incorrect, please contact an admin or
            super-admin.
          </div>
        </div>
      </div>

      {loading && (
        <div className="card profile-loading-card">
          <span>Loading latest profile data…</span>
        </div>
      )}
    </div>
  );
}
